import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useEffect, useMemo, useRef, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { api } from "../api/client";
import type {
  AgentOutputOut,
  AnalysisStatusOut,
  ArbiterOutputOut,
  AssessmentStatus,
  OutputLanguage,
  RiskLevel,
  RowPayload,
  RowStatus,
} from "../api/types";
import { Icon } from "../components/Icon";
import {
  TaxonomyText,
  replaceTaxonomyTerms,
  termLabel,
  taxonomyTermMap,
} from "../components/TaxonomyText";
import { LANGUAGE_NAME, useLang, type Lang } from "../stores/language";
import { useWizardStore } from "../stores/wizard";
import { trackEvent } from "../telemetry";

// ---------- Helpers ----------

function rowReady(s: RowStatus | undefined): boolean {
  return !!s && s.a_ready && s.b_ready && s.arbiter_ready;
}

function verdictClass(v: string | undefined): string {
  if (v === "agreed") return "sim-agreed";
  if (v === "minor") return "sim-minor";
  if (v === "divergent") return "sim-divergent";
  return "";
}

function assessmentLabel(status: AssessmentStatus): string {
  switch (status) {
    case "no_risk":
      return "Assessed · no risk";
    case "risk_identified":
      return "Risk identified";
    case "not_applicable":
      return "Not applicable";
    case "cannot_assess":
      return "Cannot assess";
    case "source_uncertain":
      return "Source uncertain";
  }
}

function defaultAssessmentForRisk(risk: RiskLevel): AssessmentStatus {
  return risk === "none" ? "no_risk" : "risk_identified";
}

function RiskBanner({
  risk,
  size = "sm",
}: {
  risk: RiskLevel;
  size?: "sm" | "lg";
}) {
  const label =
    risk === "yellow"
      ? "RISK · YELLOW"
      : risk === "orange"
        ? "RISK · ORANGE"
        : "NO RISK";
  return (
    <div
      className={
        "risk-banner risk-" + risk + (size === "lg" ? " risk-banner-lg" : "")
      }
    >
      {risk === "none" ? (
        <Icon name="checkCircle" size={12} />
      ) : (
        <Icon name="alertTri" size={12} />
      )}
      {label}
    </div>
  );
}

// Row-subject terms carry the analysis-language subject as the label and the
// other language's subject as the tooltip (taxonomy_terms.py), so the view
// language picks one of the two. Only valid for row keys, not rule terms.
function rowSubject(
  rowKey: string,
  terms: AnalysisStatusOut["terms"],
  lang: Lang,
  analysisLang: OutputLanguage,
): string {
  const term = terms.find((candidate) => candidate.id === rowKey);
  if (term && lang !== analysisLang) return term.description;
  return termLabel(rowKey, terms);
}

// ---------- Page ----------

export function WizardPage() {
  const { docId, rowKey } = useParams<{ docId: string; rowKey?: string }>();
  const navigate = useNavigate();
  const qc = useQueryClient();
  const lang = useLang();
  const [analysisId, setAnalysisId] = useState<string | null>(null);
  const previousDocumentStatus = useRef<string | null>(null);
  const readyAnalysisId = useRef<string | null>(null);
  const failedAnalysisId = useRef<string | null>(null);

  // Document + per-page OCR progress.
  const docQuery = useQuery({
    queryKey: ["document", docId],
    queryFn: () => api.getDocument(docId!),
    enabled: !!docId,
    refetchInterval: (q) =>
      q.state.data?.status === "ocr_done" ||
      q.state.data?.status === "ocr_failed"
        ? false
        : 1500,
  });

  const pagesQuery = useQuery({
    queryKey: ["pages", docId],
    queryFn: () => api.listPages(docId!),
    enabled: !!docId && docQuery.data?.status !== "uploaded",
    refetchInterval: (q) => {
      const docStatus = docQuery.data?.status;
      if (docStatus === "ocr_done" || docStatus === "ocr_failed") return false;
      const allHaveText =
        q.state.data && q.state.data.length > 0 &&
        q.state.data.every((p) => p.has_text);
      return allHaveText ? false : 1200;
    },
  });

  // Start is idempotent on the backend: an unfinished latest analysis is
  // returned instead of creating a duplicate.
  const startMutation = useMutation({
    mutationFn: () => api.startAnalysis(docId!),
    onSuccess: (a) => {
      setAnalysisId(a.id);
      trackEvent("analysis_started", {
        document_id: docId,
        analysis_id: a.id,
        resumed: a.resumed,
      });
    },
    onError: () => trackEvent("analysis_start_failed", { document_id: docId }),
  });

  useEffect(() => {
    const document = docQuery.data;
    if (!document || previousDocumentStatus.current === document.status) return;
    previousDocumentStatus.current = document.status;
    trackEvent("document_status_changed", {
      document_id: docId,
      status: document.status,
      page_count: document.page_count,
      package_document_count: document.package_document_count,
    });
  }, [docId, docQuery.data]);

  useEffect(() => {
    if (
      docQuery.data?.status === "ocr_done" &&
      !analysisId &&
      !startMutation.isPending
    ) {
      startMutation.mutate();
    }
  }, [docQuery.data?.status, analysisId, startMutation]);

  const statusQuery = useQuery({
    queryKey: ["analysis-status", analysisId],
    queryFn: () => api.analysisStatus(analysisId!),
    enabled: !!analysisId,
    refetchInterval: (q) => {
      if (!q.state.data) return 1500;
      if (q.state.data.status === "done") {
        return false;
      }
      if (q.state.data.status === "failed") return 5000;
      const allReady = q.state.data.rows.every(rowReady);
      return allReady ? false : 1500;
    },
  });

  const retryAnalysisMutation = useMutation({
    mutationFn: () => api.retryAnalysis(analysisId!),
    onMutate: () => {
      trackEvent("analysis_retry_requested", {
        document_id: docId,
        analysis_id: analysisId,
      });
    },
    onSuccess: (result) => {
      failedAnalysisId.current = null;
      trackEvent("analysis_retry_queued", {
        document_id: docId,
        analysis_id: analysisId,
        extractor_count: result.enqueued_extractors.length,
        arbiter_count: result.enqueued_arbiter_row_keys.length,
      });
      qc.invalidateQueries({ queryKey: ["analysis-status", analysisId] });
    },
    onError: () => {
      trackEvent("analysis_retry_failed", {
        document_id: docId,
        analysis_id: analysisId,
      });
    },
  });

  const rowKeys = useMemo(
    () => (statusQuery.data?.rows ?? []).map((r) => r.row_key),
    [statusQuery.data],
  );
  const statusByKey = useMemo(() => {
    const m: Record<string, RowStatus> = {};
    for (const r of statusQuery.data?.rows ?? []) m[r.row_key] = r;
    return m;
  }, [statusQuery.data]);

  useEffect(() => {
    if (
      !analysisId ||
      !statusQuery.data ||
      readyAnalysisId.current === analysisId ||
      !statusQuery.data.rows.every(rowReady)
    ) {
      return;
    }
    readyAnalysisId.current = analysisId;
    trackEvent("analysis_ready", {
      document_id: docId,
      analysis_id: analysisId,
      row_count: statusQuery.data.rows.length,
    });
  }, [analysisId, docId, statusQuery.data]);

  useEffect(() => {
    if (
      !analysisId ||
      statusQuery.data?.status !== "failed" ||
      failedAnalysisId.current === analysisId
    ) {
      return;
    }
    failedAnalysisId.current = analysisId;
    trackEvent("analysis_failed", {
      document_id: docId,
      analysis_id: analysisId,
      incomplete_row_count: statusQuery.data.rows.filter((row) => !rowReady(row)).length,
    });
  }, [analysisId, docId, statusQuery.data]);

  // Wizard store — track approvals per analysis. Reset when analysis changes.
  const setActiveAnalysis = useWizardStore((s) => s.setActiveAnalysis);
  const setRow = useWizardStore((s) => s.setRow);
  const rowStatesByKey = useWizardStore((s) => s.rowStates);
  useEffect(() => {
    if (analysisId) setActiveAnalysis(analysisId);
  }, [analysisId, setActiveAnalysis]);

  useEffect(() => {
    if (!analysisId || !statusQuery.data) return;
    for (const row of statusQuery.data.rows) {
      if (row.reviewed && !rowStatesByKey[row.row_key]?.approved) {
        setRow(row.row_key, { approved: true });
      }
    }
  }, [analysisId, rowStatesByKey, setRow, statusQuery.data]);

  // Any row can be opened in any order once its outputs are ready (client
  // feedback: checking one topic should not mean clicking through all of
  // them). With no row in the URL, open the first row not yet approved.
  const firstUnapprovedKey = useMemo(
    () => rowKeys.find((k) => !rowStatesByKey[k]?.approved),
    [rowKeys, rowStatesByKey],
  );
  const allApproved = rowKeys.length > 0 && firstUnapprovedKey === undefined;
  const currentRowKey =
    rowKey && rowKeys.includes(rowKey) ? rowKey : firstUnapprovedKey ?? rowKeys[0];
  const currentIdx = currentRowKey ? rowKeys.indexOf(currentRowKey) : -1;
  const currentReady = currentRowKey
    ? rowReady(statusByKey[currentRowKey])
    : false;

  const rowQuery = useQuery({
    queryKey: ["row", analysisId, currentRowKey, lang],
    queryFn: () => api.getRow(analysisId!, currentRowKey!, lang),
    enabled: !!analysisId && !!currentRowKey && currentReady,
    // Switching language keeps the row on screen while it is translated;
    // switching rows must not show the previous row.
    placeholderData: (previous, previousQuery) =>
      previousQuery?.queryKey[2] === currentRowKey ? previous : undefined,
  });

  // Done — go to report.
  useEffect(() => {
    if (allApproved && analysisId && !rowKey) {
      navigate(`/docs/${docId}/report`, { replace: true });
    }
  }, [allApproved, analysisId, docId, navigate, rowKey]);

  if (!docId) return null;

  // ---------- Phase view (loading) ----------
  // Show the phases screen until the *current* (first-unapproved) row is
  // fully ready. This means: on a fresh analysis we stay here until row 1 is
  // done — even if later rows happen to finish first.

  const analysisFailed = statusQuery.data?.status === "failed";
  const showPhases =
    !docQuery.data ||
    docQuery.data.status !== "ocr_done" ||
    !analysisId ||
    !statusQuery.data ||
    !currentRowKey ||
    !currentReady ||
    analysisFailed;

  if (showPhases) {
    return (
      <ProcessingPhases
        docStatus={docQuery.data?.status}
        pages={pagesQuery.data ?? []}
        pageCount={docQuery.data?.page_count ?? null}
        analysisStatus={statusQuery.data ?? null}
        waitingForRowKey={currentRowKey}
        retryPending={retryAnalysisMutation.isPending}
        retryError={retryAnalysisMutation.error?.message ?? null}
        onRetryAnalysis={() => retryAnalysisMutation.mutate()}
      />
    );
  }

  return (
    <div style={{ maxWidth: 1080, margin: "0 auto", padding: "8px 24px" }}>
      <WizardStrip
        rows={rowKeys}
        labelFor={(k) => rowSubject(k, statusQuery.data!.terms, lang, statusQuery.data!.output_language)}
        statusByKey={statusByKey}
        approvedByKey={rowStatesByKey}
        currentIdx={currentIdx}
        onSelect={(k) => navigate(`/docs/${docId}/wizard/${k}`)}
        onReport={() => navigate(`/docs/${docId}/report`)}
      />
      {rowQuery.data ? (
        <RowReview
          payload={rowQuery.data}
          analysisLanguage={statusQuery.data.output_language}
          translating={rowQuery.isFetching && lang !== statusQuery.data.output_language}
          analysisId={analysisId!}
          rowKey={currentRowKey!}
          isLastRow={currentIdx === rowKeys.length - 1}
          onNext={() => {
            const next = rowKeys[currentIdx + 1];
            if (next) navigate(`/docs/${docId}/wizard/${next}`);
            else navigate(`/docs/${docId}/report`);
            qc.invalidateQueries({ queryKey: ["row", analysisId] });
          }}
          onBack={() => {
            const prev = rowKeys[currentIdx - 1];
            if (prev) navigate(`/docs/${docId}/wizard/${prev}`);
          }}
          canGoBack={currentIdx > 0}
        />
      ) : (
        <div className="card card-pad" style={{ marginTop: 16 }}>
          <Icon name="refresh" size={16} className="spin" />{" "}
          {lang === statusQuery.data.output_language ? "Loading row…" : "Loading and translating row…"}
        </div>
      )}
    </div>
  );
}

// ---------- Phases (loading screen) ----------

function ProcessingPhases({
  docStatus,
  pages,
  pageCount,
  analysisStatus,
  waitingForRowKey,
  retryPending,
  retryError,
  onRetryAnalysis,
}: {
  docStatus: string | undefined;
  pages: Array<{
    page_number: number;
    has_text: boolean;
    char_count: number;
    preview: string;
  }>;
  pageCount: number | null;
  analysisStatus: AnalysisStatusOut | null;
  waitingForRowKey?: string;
  retryPending: boolean;
  retryError: string | null;
  onRetryAnalysis: () => void;
}) {
  const lang = useLang();
  const uploadDone = docStatus !== undefined && docStatus !== "uploaded";
  const ocrDone = docStatus === "ocr_done";
  const ocrFailed = docStatus === "ocr_failed";
  const ocrActive = docStatus === "ocr_running";

  const totalPages = pageCount ?? (pages.length > 0 ? pages.length : null);
  const donePages = pages.filter((p) => p.has_text).length;
  const ocrPct =
    totalPages && totalPages > 0
      ? Math.round((donePages / totalPages) * 100)
      : 0;

  // "Waiting for row X" — the row the user actually needs next.
  const targetRow = analysisStatus?.rows.find(
    (r) => r.row_key === waitingForRowKey,
  );
  const analysisFailed = analysisStatus?.status === "failed";
  const analysisActive =
    !analysisFailed && ocrDone && !!targetRow && !rowReady(targetRow);
  const analysisDone = !analysisFailed && !!targetRow && rowReady(targetRow);
  const extractorFailed = analysisStatus?.error_message?.startsWith("Extractor ") ?? false;

  const totalSlots = (analysisStatus?.rows.length ?? 7) * 3;
  const filledSlots = (analysisStatus?.rows ?? []).reduce(
    (acc, r) =>
      acc + (r.a_ready ? 1 : 0) + (r.b_ready ? 1 : 0) + (r.arbiter_ready ? 1 : 0),
    0,
  );
  const analysisPct =
    totalSlots > 0 ? Math.round((filledSlots / totalSlots) * 100) : 0;
  let analysisSub = "Waiting for OCR…";
  if (analysisFailed) {
    analysisSub = extractorFailed
      ? "Analysis paused because an extractor returned unsafe report text."
      : "Analysis paused after a processing failure.";
  } else if (analysisDone) {
    analysisSub = "Row ready — opening review…";
  } else if (analysisActive) {
    analysisSub = `Outputs ready ${filledSlots} / ${totalSlots} across all rows…`;
  } else if (ocrDone) {
    analysisSub = "Starting…";
  }

  return (
    <div className="phases">
      <Phase
        n={1}
        title="Upload"
        sub={uploadDone ? "File received and stored." : "Receiving file…"}
        active={!uploadDone}
        done={uploadDone}
      />

      <Phase
        n={2}
        title="OCR — extracting text from PDF"
        sub={
          ocrFailed
            ? "OCR failed."
            : ocrDone
              ? `Extracted ${donePages} pages.`
              : ocrActive
                ? totalPages
                  ? `Reading pages ${donePages} / ${totalPages}…`
                  : "Reading pages…"
                : "Waiting…"
        }
        active={ocrActive}
        done={ocrDone}
      >
        {(ocrActive || ocrDone) && totalPages !== null && (
          <>
            <div className="phase-progress">
              <div style={{ width: `${ocrPct}%` }} />
            </div>
            <div className="ocr-pages">
              {Array.from({ length: totalPages }).map((_, i) => {
                const n = i + 1;
                const p = pages.find((x) => x.page_number === n);
                return (
                  <div
                    key={n}
                    className={"ocr-page" + (p?.has_text ? "" : " pending")}
                  >
                    <span className="pg-num">p.{n}</span>
                    {p?.has_text ? (
                      p.preview
                    ) : (
                      <span>
                        <Icon name="refresh" size={10} className="spin" />{" "}
                        extracting…
                      </span>
                    )}
                  </div>
                );
              })}
            </div>
          </>
        )}
      </Phase>

      <Phase
        n={3}
        title={
          waitingForRowKey && analysisStatus
            ? `Analysis — preparing ${rowSubject(waitingForRowKey, analysisStatus.terms, lang, analysisStatus.output_language)}`
            : "Analysis — two extractors + arbiter per row"
        }
        sub={analysisSub}
        active={analysisActive}
        done={analysisDone}
        failed={analysisFailed}
      >
        {analysisStatus && (
          <>
            <div className="phase-progress">
              <div style={{ width: `${analysisPct}%` }} />
            </div>
            <div className="analysis-rows">
              {analysisStatus.rows.map((r) => (
                <div key={r.row_key} className="analysis-row">
                  <span className="name">
                    {rowSubject(r.row_key, analysisStatus.terms, lang, analysisStatus.output_language)}
                  </span>
                  <span
                    className={"step " + (r.a_ready ? "done" : "pending")}
                  >
                    A {r.a_ready ? "✓" : "…"}
                  </span>
                  <span
                    className={"step " + (r.b_ready ? "done" : "pending")}
                  >
                    B {r.b_ready ? "✓" : "…"}
                  </span>
                  <span
                    className={
                      "step " + (r.arbiter_ready ? "done" : "pending")
                    }
                  >
                    R {r.arbiter_ready ? "✓" : "…"}
                  </span>
                </div>
              ))}
            </div>
            {analysisFailed && (
              <div className="analysis-failure" role="alert">
                <div className="analysis-failure-title">
                  <Icon name="alertTri" size={14} /> Analysis processing stopped
                </div>
                <div>{analysisStatus.error_message ?? "The analysis did not complete."}</div>
                <button
                  className="btn btn-secondary btn-sm"
                  type="button"
                  disabled={retryPending}
                  onClick={onRetryAnalysis}
                >
                  {retryPending ? "Queuing retry…" : "Retry incomplete work"}
                </button>
                {retryError && <div className="analysis-retry-error">{retryError}</div>}
              </div>
            )}
          </>
        )}
      </Phase>
    </div>
  );
}

function Phase({
  n,
  title,
  sub,
  active,
  done,
  failed = false,
  children,
}: {
  n: number;
  title: string;
  sub: string;
  active: boolean;
  done: boolean;
  failed?: boolean;
  children?: React.ReactNode;
}) {
  return (
    <div
      className={
        "phase" +
        (active ? " active" : "") +
        (done ? " done" : "") +
        (failed ? " failed" : "")
      }
    >
      <div className="phase-mark">
        {done ? (
          <Icon name="check" size={14} />
        ) : failed ? (
          <Icon name="alertTri" size={14} />
        ) : (
          n
        )}
      </div>
      <div className="phase-body">
        <div className="phase-title">{title}</div>
        <div className="phase-sub">
          {active && <Icon name="refresh" size={11} className="spin" />}{" "}
          {sub}
        </div>
        {children}
      </div>
    </div>
  );
}

// ---------- Wizard strip (free nav) ----------

function WizardStrip({
  rows,
  labelFor,
  statusByKey,
  approvedByKey,
  currentIdx,
  onSelect,
  onReport,
}: {
  rows: string[];
  labelFor: (rowKey: string) => string;
  statusByKey: Record<string, RowStatus>;
  approvedByKey: Record<string, { approved: boolean }>;
  currentIdx: number;
  onSelect: (k: string) => void;
  onReport: () => void;
}) {
  return (
    <div className="strip">
      {rows.map((rk, i) => {
        const st = statusByKey[rk];
        const ready = rowReady(st);
        const approved = approvedByKey[rk]?.approved === true;
        const clickable = ready;
        const label = labelFor(rk);
        const tooltip = approved
          ? `${label} (approved)`
          : ready
            ? label
            : `${label} — waiting for agents`;
        return (
          <button
            key={rk}
            className={
              "strip-step " +
              (i === currentIdx ? "active " : "") +
              (approved ? "done " : "")
            }
            onClick={() => {
              if (clickable) onSelect(rk);
            }}
            disabled={!clickable}
            title={tooltip}
            style={!clickable ? { cursor: "not-allowed", opacity: 0.55 } : undefined}
          >
            <span className="strip-num">
              {approved ? <Icon name="check" size={12} /> : i + 1}
            </span>
            <span className="strip-label">{label}</span>
          </button>
        );
      })}
      <button className="strip-step strip-report" onClick={onReport} title="Open the report">
        <span className="strip-num">
          <Icon name="file" size={12} />
        </span>
        <span className="strip-label">Report</span>
      </button>
    </div>
  );
}

// ---------- Row not ready (gate inside wizard) ----------

// ---------- Row review ----------

function RowReview({
  payload,
  analysisLanguage,
  translating,
  analysisId,
  rowKey,
  isLastRow,
  onNext,
  onBack,
  canGoBack,
}: {
  payload: RowPayload;
  analysisLanguage: OutputLanguage;
  translating: boolean;
  analysisId: string;
  rowKey: string;
  isLastRow: boolean;
  onNext: () => void;
  onBack: () => void;
  canGoBack: boolean;
}) {
  const lang = useLang();
  const wizardRow = useWizardStore((s) => s.rowStates[rowKey]);
  const setRow = useWizardStore((s) => s.setRow);
  const qc = useQueryClient();
  const [reanalysisBaseline, setReanalysisBaseline] = useState<{
    a: string | null;
    b: string | null;
    arbiter: string | null;
  } | null>(null);

  useEffect(() => {
    const openedAt = performance.now();
    trackEvent("review_row_opened", {
      analysis_id: analysisId,
      row_key: rowKey,
      has_existing_review: Boolean(payload.review),
      arbiter_verdict: payload.arbiter?.verdict,
      similarity_score: payload.arbiter?.similarity_score,
    });
    return () => {
      trackEvent("review_row_left", {
        analysis_id: analysisId,
        row_key: rowKey,
        duration_ms: Math.round(performance.now() - openedAt),
      });
    };
  }, [analysisId, rowKey]);

  useEffect(() => {
    if (!payload.review) return;
    setRow(rowKey, {
      selected: payload.review.chosen ?? "custom",
      customDesc: replaceTaxonomyTerms(payload.review.final_description, payload.terms),
      customComm: replaceTaxonomyTerms(payload.review.final_comments, payload.terms),
      finalRisk: payload.review.final_risk_level,
      finalAssessmentStatus: payload.review.final_assessment_status,
      sourceOutputId: payload.review.chosen_agent_output_id,
      approved: true,
    });
  }, [payload.review, rowKey, setRow]);

  useEffect(() => {
    if (!reanalysisBaseline) return;
    const timer = window.setInterval(async () => {
      const next = await qc.fetchQuery({
        queryKey: ["row", analysisId, rowKey, lang],
        queryFn: () => api.getRow(analysisId, rowKey, lang),
      });
      const changed =
        next.agent_a?.id !== reanalysisBaseline.a &&
        next.agent_b?.id !== reanalysisBaseline.b &&
        next.arbiter?.id !== reanalysisBaseline.arbiter;
      if (changed && next.agent_a && next.agent_b && next.arbiter) {
        setReanalysisBaseline(null);
        trackEvent("reanalysis_completed", {
          analysis_id: analysisId,
          row_key: rowKey,
          arbiter_verdict: next.arbiter.verdict,
          similarity_score: next.arbiter.similarity_score,
        });
        const selected = wizardRow?.selected;
        const fresh = selected === "B" ? next.agent_b : next.agent_a;
        if (selected !== "custom" && fresh) {
          setRow(rowKey, {
            customDesc: replaceTaxonomyTerms(fresh.description, next.terms),
            customComm: replaceTaxonomyTerms(fresh.comments, next.terms),
            finalRisk: fresh.risk_level,
            finalAssessmentStatus: fresh.assessment_status,
            sourceOutputId: fresh.id,
          });
        }
      }
    }, 1500);
    return () => window.clearInterval(timer);
  }, [analysisId, lang, qc, reanalysisBaseline, rowKey, setRow, wizardRow?.selected]);

  const reanalyze = useMutation({
    mutationFn: (comment: string) =>
      api.reanalyzeRow(analysisId, rowKey, comment),
    onMutate: (comment) => {
      trackEvent("reanalysis_requested", {
        analysis_id: analysisId,
        row_key: rowKey,
        comment_length: comment.length,
      });
    },
    onSuccess: () => {
      trackEvent("reanalysis_queued", {
        analysis_id: analysisId,
        row_key: rowKey,
      });
      setReanalysisBaseline({
        a: payload.agent_a?.id ?? null,
        b: payload.agent_b?.id ?? null,
        arbiter: payload.arbiter?.id ?? null,
      });
    },
    onError: () => {
      trackEvent("reanalysis_failed", {
        analysis_id: analysisId,
        row_key: rowKey,
      });
    },
  });

  const submit = useMutation({
    mutationFn: async () => {
      const selected = wizardRow?.selected ?? "A";
      const chosenOutput =
        selected === "A"
          ? payload.agent_a
          : selected === "B"
            ? payload.agent_b
            : null;
      const finalDescription = wizardRow?.customDesc ?? "";
      const finalComments = wizardRow?.customComm ?? "";
      const finalRisk = wizardRow?.finalRisk ?? "none";
      const finalAssessmentStatus =
        wizardRow?.finalAssessmentStatus ?? defaultAssessmentForRisk(finalRisk);
      const edited =
        !!chosenOutput &&
        (chosenOutput.description !== finalDescription ||
          chosenOutput.comments !== finalComments ||
          chosenOutput.risk_level !== finalRisk ||
          chosenOutput.assessment_status !== finalAssessmentStatus);
      const decision = selected === "custom" ? "wrote_own" : edited ? "edited" : "approved";
      await api.submitReview(analysisId, rowKey, {
        decision,
        chosen: selected,
        chosen_agent_output_id: chosenOutput?.id ?? wizardRow?.sourceOutputId ?? null,
        final_description: finalDescription,
        final_comments: finalComments,
        final_risk_level: finalRisk,
        final_assessment_status: finalAssessmentStatus,
        what_was_wrong: [],
        reason_optional: null,
      });
      return {
        selected,
        decision,
        finalRisk,
        finalAssessmentStatus,
        descriptionLength: finalDescription.length,
        commentsLength: finalComments.length,
      };
    },
    onSuccess: (result) => {
      trackEvent("review_submitted", {
        analysis_id: analysisId,
        row_key: rowKey,
        selected: result.selected,
        decision: result.decision,
        final_risk: result.finalRisk,
        final_assessment_status: result.finalAssessmentStatus,
        description_length: result.descriptionLength,
        comments_length: result.commentsLength,
      });
      if (isLastRow) {
        trackEvent("review_completed", {
          analysis_id: analysisId,
        });
      }
      // Mark this row approved in the local store so the next row unlocks
      // and the strip ticks. Then advance.
      setRow(rowKey, { approved: true });
      onNext();
    },
    onError: () => {
      trackEvent("review_submit_failed", {
        analysis_id: analysisId,
        row_key: rowKey,
      });
    },
  });

  const selectOutput = (selected: "A" | "B" | "custom") => {
    const output = selected === "A" ? payload.agent_a : selected === "B" ? payload.agent_b : null;
    setRow(rowKey, {
      selected,
      customDesc: output
        ? replaceTaxonomyTerms(output.description, payload.terms)
        : wizardRow?.customDesc ?? "",
      customComm: output
        ? replaceTaxonomyTerms(output.comments, payload.terms)
        : wizardRow?.customComm ?? "",
      finalRisk: output?.risk_level ?? wizardRow?.finalRisk ?? "none",
      finalAssessmentStatus:
        output?.assessment_status ?? wizardRow?.finalAssessmentStatus ?? "cannot_assess",
      sourceOutputId: output?.id ?? wizardRow?.sourceOutputId ?? null,
      approved: false,
    });
    trackEvent("review_output_selected", {
      analysis_id: analysisId,
      row_key: rowKey,
      selected,
    });
  };

  const setOverride = (enabled: boolean) => {
    if (enabled) {
      selectOutput("custom");
      return;
    }
    const selected =
      payload.agent_b?.id === wizardRow?.sourceOutputId || !payload.agent_a ? "B" : "A";
    selectOutput(selected);
  };

  // Shown text only. Choosing an answer copies the original (English) text.
  const tr = (text: string) => payload.translations[text] ?? text;
  const translated = lang !== analysisLanguage;

  const arbiter = payload.arbiter;
  const sim = arbiter?.similarity_score ?? null;
  const simClass = verdictClass(arbiter?.verdict);

  return (
    <div>
      {/* Row header — always shows similarity + verdict */}
      <div className="row-head" style={{ marginTop: 18 }}>
        <div>
          <div className="row-eyebrow">Analysis item</div>
          <h2 className="row-title">
            {lang === "pl" ? payload.subject_pl : payload.subject_en}
          </h2>
          <div className="row-meta">
            <span>{lang === "pl" ? payload.subject_en : payload.subject_pl}</span>
          </div>
        </div>
        <div
          style={{
            display: "flex",
            alignItems: "center",
            gap: 12,
            flexWrap: "wrap",
            justifyContent: "flex-end",
          }}
        >
          <span className={"sim-pill " + simClass}>
            {sim !== null ? `${sim}%` : "—"} agreement
          </span>
          {arbiter && (
            <span className={"sim-pill " + simClass}>
              <Icon name="info" size={12} /> {arbiter.verdict}
            </span>
          )}
        </div>
      </div>

      {translated && (
        <div className="translation-note" role="note">
          <Icon name={translating ? "refresh" : "info"} size={14} className={translating ? "spin" : undefined} />{" "}
          {translating
            ? "Translating…"
            : payload.translation_error
              ? "Translation is unavailable right now; showing the original text."
              : `Model text is machine-translated from the ${LANGUAGE_NAME[analysisLanguage]} original. Switch to ${analysisLanguage.toUpperCase()} to see it.`}
        </div>
      )}

      {/* Arbiter banner — always rendered when arbiter exists. */}
      <ArbiterBanner arbiter={arbiter} terms={payload.terms} tr={tr} />

      {/* Side-by-side agents */}
      <div className="agents-grid">
        <AgentCard
          letter="A"
          data={payload.agent_a}
          selected={wizardRow?.selected === "A"}
          onSelect={() => selectOutput("A")}
          terms={payload.terms}
          tr={tr}
        />
        <AgentCard
          letter="B"
          data={payload.agent_b}
          selected={wizardRow?.selected === "B"}
          onSelect={() => selectOutput("B")}
          terms={payload.terms}
          tr={tr}
        />
      </div>

      <div className="card card-pad final-answer">
        <div className="final-answer-head">
          <div>
            <div className="field-label">Final reviewer answer</div>
            <div className="card-sub">
              Choose an agent as a base or make a one-time override. You can edit every field.
            </div>
          </div>
          <label className="override-toggle">
            <input
              type="checkbox"
              checked={wizardRow?.selected === "custom"}
              onChange={(event) => setOverride(event.target.checked)}
            />
            <span>One-time override</span>
          </label>
        </div>
        {translated && (
          <div className="card-sub" style={{ marginBottom: 14 }}>
            {`You edit the ${LANGUAGE_NAME[analysisLanguage]} original. The report in the other language is translated from it.`}
          </div>
        )}
        {wizardRow?.selected === "custom" && (
          <div className="card-sub" style={{ marginBottom: 14 }}>
            This override applies only to this lease analysis row. It does not change the rubric or train the models.
          </div>
        )}
        <label className="field-label">Description</label>
        <textarea
          className="field"
          value={wizardRow?.customDesc ?? ""}
          onChange={(e) => setRow(rowKey, { customDesc: e.target.value, approved: false })}
          rows={4}
        />
        <label className="field-label mt-3">Proposed report comment</label>
        <textarea
          className="field"
          value={wizardRow?.customComm ?? ""}
          onChange={(e) => setRow(rowKey, { customComm: e.target.value, approved: false })}
          rows={4}
        />
        <label className="field-label mt-3">Assessment status</label>
        <select
          className="field"
          value={wizardRow?.finalAssessmentStatus ?? "cannot_assess"}
          onChange={(e) => {
            const assessmentStatus = e.target.value as AssessmentStatus;
            const currentRisk = wizardRow?.finalRisk ?? "none";
            setRow(rowKey, {
              finalAssessmentStatus: assessmentStatus,
              finalRisk:
                assessmentStatus === "risk_identified"
                  ? currentRisk === "none" ? "yellow" : currentRisk
                  : assessmentStatus === "source_uncertain"
                    ? currentRisk
                    : "none",
              approved: false,
            });
          }}
        >
          <option value="no_risk">Assessed — no risk found</option>
          <option value="risk_identified">Risk identified</option>
          <option value="not_applicable">Not applicable</option>
          <option value="cannot_assess">Cannot assess — missing data</option>
          <option value="source_uncertain">Source uncertain</option>
        </select>
        <label className="field-label mt-3">Final risk</label>
        <select
          className="field"
          value={wizardRow?.finalRisk ?? "none"}
          onChange={(e) => {
            const risk = e.target.value as RiskLevel;
            setRow(rowKey, {
              finalRisk: risk,
              finalAssessmentStatus:
                wizardRow?.finalAssessmentStatus === "source_uncertain"
                  ? "source_uncertain"
                  : defaultAssessmentForRisk(risk),
              approved: false,
            });
          }}
        >
          <option value="none">No risk</option>
          <option value="yellow">Yellow</option>
          <option value="orange">Orange</option>
        </select>
      </div>

      {reanalysisBaseline && (
        <div className="reanalyze-card">
          <Icon name="refresh" size={16} className="spin" />{" "}
          Re-analysis is running. New extractor and arbiter outputs will appear automatically.
        </div>
      )}

      {/* Comment + reanalyze */}
      <div className="comment-row" style={{ marginTop: 18 }}>
        <div className="comment-box">
          <div className="field-label">Comment for the agents (optional)</div>
          <textarea
            className="field"
            style={{ minHeight: 70 }}
            placeholder="Add a note or correction — agents will re-extract if you re-analyze."
            value={wizardRow?.comment ?? ""}
            onChange={(e) => setRow(rowKey, { comment: e.target.value })}
          />
        </div>
        <button
          className="btn btn-secondary btn-lg"
          disabled={!wizardRow?.comment || reanalyze.isPending || !!reanalysisBaseline}
          onClick={() => reanalyze.mutate(wizardRow!.comment)}
        >
          <Icon name="refresh" size={14} /> Re-analyze with comment
        </button>
      </div>

      {!wizardRow?.selected && (
        <div className="select-hint" role="note">
          <Icon name="info" size={14} />{" "}
          Select the answer of Extractor A or B above, or use a one-time override, to continue.
        </div>
      )}

      <div className="row-footer">
        <button
          className="btn btn-secondary"
          onClick={onBack}
          disabled={!canGoBack}
          style={!canGoBack ? { opacity: 0.5, cursor: "not-allowed" } : undefined}
        >
          <Icon name="back" size={14} /> Previous row
        </button>
        <button
          className="btn btn-primary btn-lg"
          disabled={
            !wizardRow?.selected ||
            !wizardRow.customDesc.trim() ||
            !wizardRow.customComm.trim() ||
            submit.isPending ||
            !!reanalysisBaseline
          }
          onClick={() => submit.mutate()}
        >
          <Icon name="check" size={14} /> Approve & continue
        </button>
      </div>
    </div>
  );
}

function ArbiterBanner({
  arbiter,
  terms,
  tr,
}: {
  arbiter: ArbiterOutputOut | null;
  terms: RowPayload["terms"];
  tr: (text: string) => string;
}) {
  if (!arbiter) {
    return (
      <div className="verdict-banner">
        <div className="verdict-avatar">R</div>
        <div style={{ flex: 1 }}>
          <div className="verdict-eyebrow">Arbiter</div>
          <div className="verdict-text" style={{ color: "var(--ink-3)" }}>
            <Icon name="refresh" size={12} className="spin" />{" "}
            Judging agreement between Extractor A and Extractor B…
          </div>
        </div>
      </div>
    );
  }
  return (
    <div className="verdict-banner">
      <div className="verdict-avatar">R</div>
      <div style={{ flex: 1 }}>
        <div className="verdict-eyebrow">
          Arbiter · verdict {arbiter.verdict} · agreement {arbiter.similarity_score}%
        </div>
        <div className="verdict-text">
          <TaxonomyText text={tr(arbiter.reviewer_note)} terms={terms} />
        </div>
        {arbiter.flags.length > 0 && (
          <ul className="verdict-flags">
            {arbiter.flags.map((flag, i) => {
              const term = taxonomyTermMap(terms).get(flag);
              return (
                <li key={i}>
                  <Icon name="alertTri" size={11} />
                  {term ? (
                    <span className="taxonomy-term" tabIndex={0}>
                      {term.label}
                      <span className="taxonomy-tooltip" role="tooltip">
                        {term.description}
                      </span>
                    </span>
                  ) : (
                    <TaxonomyText text={tr(flag)} terms={terms} />
                  )}
                </li>
              );
            })}
          </ul>
        )}
      </div>
    </div>
  );
}

function AgentCard({
  letter,
  data,
  selected,
  onSelect,
  terms,
  tr,
}: {
  letter: "A" | "B";
  data: AgentOutputOut | null;
  selected: boolean;
  onSelect: () => void;
  terms: RowPayload["terms"];
  tr: (text: string) => string;
}) {
  return (
    <div
      className={"agent-col" + (selected ? " selected" : "")}
      onClick={onSelect}
    >
      <div className="agent-col-head">
        <div
          className="agent-letter"
          style={{
            background:
              letter === "A"
                ? "linear-gradient(135deg, #d6eaf5, #2f7fc1)"
                : "linear-gradient(135deg, #d6ead9, #2f7a4a)",
          }}
        >
          {letter}
        </div>
        <div style={{ flex: 1, minWidth: 0 }}>
          <div className="agent-col-name">Extractor Agent {letter}</div>
          <div className="agent-col-sub">
            {data
              ? `pages: ${data.source_pages.join(", ") || "?"} · confidence ${(data.confidence * 100).toFixed(0)}%`
              : "waiting…"}
          </div>
        </div>
        {data && (
          <button
            type="button"
            className={`btn btn-sm ${selected ? "btn-primary" : "btn-secondary"}`}
            aria-pressed={selected}
            onClick={(event) => {
              event.stopPropagation();
              onSelect();
            }}
          >
            {selected ? (
              <>
                <Icon name="check" size={12} /> Selected answer
              </>
            ) : (
              "Select this answer"
            )}
          </button>
        )}
      </div>
      <div className="agent-col-body">
        {data ? (
          <>
            <RiskBanner risk={data.risk_level} size="lg" />
            <div className={`assessment-badge assessment-${data.assessment_status}`}>
              {assessmentLabel(data.assessment_status)}
            </div>
            <div className="field-label mt-3">Description</div>
            <div className="field-val">
              <TaxonomyText text={tr(data.description)} terms={terms} />
            </div>
            <div className="field-label mt-3">Proposed report comment</div>
            <div className="field-val">
              <TaxonomyText text={tr(data.comments)} terms={terms} />
            </div>
            {data.explanation && (
              <details className="explanation" open onClick={(event) => event.stopPropagation()}>
                <summary className="field-label">Explanation</summary>
                <div className="internal-triggers-note">Reviewer-only reasoning. Not included in the report.</div>
                <div className="field-val">
                  <TaxonomyText text={tr(data.explanation)} terms={terms} />
                </div>
              </details>
            )}
            <div className="internal-triggers">
              <div className="field-label">Internal matched triggers</div>
              <div className="internal-triggers-note">Not included in the client report.</div>
              {data.matched_trigger_ids.length > 0 ? (
                <ul>
                  {data.matched_trigger_ids.map((id) => {
                    const term = taxonomyTermMap(terms).get(id);
                    return (
                      <li key={id}>
                        {term ? (
                          <span className="taxonomy-term" tabIndex={0}>
                            {term.label}
                            <span className="taxonomy-tooltip" role="tooltip">
                              {term.description}
                            </span>
                          </span>
                        ) : (
                          termLabel(id, terms)
                        )}
                      </li>
                    );
                  })}
                </ul>
              ) : (
                <div className="internal-triggers-empty">No listed trigger matched.</div>
              )}
            </div>
          </>
        ) : (
          <div style={{ color: "var(--ink-3)", fontSize: 12 }}>
            <Icon name="refresh" size={12} className="spin" /> Extracting…
          </div>
        )}
      </div>
    </div>
  );
}
